const isMobile = () => window.innerWidth <= 768;

class MobileVideoAutoplayController {
  #domObserver = null;
  #abortController = new AbortController();

  constructor() {
    this.items = [];
    this.observers = new Map();
    this.mobile = isMobile();
    this.init();
  }

  init() {
    this.collect();
    if (this.mobile) this.observe();
    this.bind();
  }

  collect() {
    const targets = document.querySelectorAll('deferred-media[data-mobile-autoplay="true"]');
    const prmReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.items = Array.from(targets).map((el) => {
      const threshold = parseFloat(el.dataset.autoplayThreshold || el.dataset.threshold || '0.5');
      const preloadMargin = el.dataset.preloadMargin || '120px';
      const pauseWhenHidden = el.dataset.pauseWhenHidden !== 'false';
      const respectPrm = el.dataset.respectPrm !== 'false';
      const wifiOnly = el.dataset.wifiOnly === 'true';
      const disabled = respectPrm && prmReduce;
      return { el, loaded: !!el.getAttribute('data-media-loaded'), playing: false, threshold, preloadMargin, pauseWhenHidden, respectPrm, wifiOnly, disabled };
    });
  }

  observe() {
    this.items.forEach((item) => {
      if (item.disabled) return;
      const options = { root: null, rootMargin: item.preloadMargin, threshold: [0, item.threshold, 1] };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== item.el) return;
          if (entry.isIntersecting && entry.intersectionRatio >= item.threshold) {
            this.play(item);
          } else {
            this.pause(item);
          }
        });
      }, options);
      io.observe(item.el);
      this.observers.set(item.el, io);
    });
  }

  async play(item) {
    if (item.wifiOnly) {
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (c && c.effectiveType && c.effectiveType !== '4g') return;
    }
    if (!item.loaded) {
      if (typeof item.el.showDeferredMedia === 'function') item.el.showDeferredMedia();
      item.loaded = true;
    } else {
      if (typeof item.el.playMedia === 'function') item.el.playMedia();
    }
    item.playing = true;
  }

  pause(item) {
    if (!item.playing) return;
    if (typeof item.el.pauseMedia === 'function') item.el.pauseMedia();
    item.playing = false;
  }

  pauseAll() {
    this.items.forEach((i) => this.pause(i));
  }

  pauseAllHiddenAware() {
    this.items.forEach((i) => { if (i.pauseWhenHidden) this.pause(i); });
  }

  reset() {
    this.pauseAll();
    if (this.observers && this.observers.size) {
      this.observers.forEach((io) => io.disconnect());
      this.observers.clear();
    }
    if (this.#domObserver) {
      this.#domObserver.disconnect();
      this.#domObserver = null;
    }
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.items = [];
    this.collect();
    if (this.mobile) this.observe();
    this.bind();
  }

  bind() {
    const signal = this.#abortController.signal;
    window.addEventListener('resize', () => {
      const now = isMobile();
      if (now !== this.mobile) {
        this.mobile = now;
        this.pauseAll();
        this.reset();
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAllHiddenAware();
    }, { signal });

    if (!this.#domObserver) {
      this.#domObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;
            const candidates = node.matches?.('deferred-media[data-mobile-autoplay="true"]')
              ? [node]
              : Array.from(node.querySelectorAll?.('deferred-media[data-mobile-autoplay="true"]') || []);
            for (const el of candidates) {
              const exists = this.items.some((i) => i.el === el);
              if (!exists) {
                const threshold = parseFloat(el.dataset.autoplayThreshold || el.dataset.threshold || '0.5');
                const preloadMargin = el.dataset.preloadMargin || '120px';
                const pauseWhenHidden = el.dataset.pauseWhenHidden !== 'false';
                const respectPrm = el.dataset.respectPrm !== 'false';
                const wifiOnly = el.dataset.wifiOnly === 'true';
                const prmReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                const disabled = respectPrm && prmReduce;
                const item = { el, loaded: !!el.getAttribute('data-media-loaded'), playing: false, threshold, preloadMargin, pauseWhenHidden, respectPrm, wifiOnly, disabled };
                this.items.push(item);
                if (this.mobile && !item.disabled) {
                  const options = { root: null, rootMargin: item.preloadMargin, threshold: [0, item.threshold, 1] };
                  const io = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                      if (entry.target !== item.el) return;
                      if (entry.isIntersecting && entry.intersectionRatio >= item.threshold) {
                        this.play(item);
                      } else {
                        this.pause(item);
                      }
                    });
                  }, options);
                  io.observe(item.el);
                  this.observers.set(item.el, io);
                }
              }
            }
          }
        }
      });
      this.#domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }
}

const start = () => {
  if (document.querySelector('deferred-media[data-mobile-autoplay="true"]')) {
    new MobileVideoAutoplayController();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}